import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, CheckCircle2 } from 'lucide-react';

const ExpandableFramework = ({ framework }) => {
  const [activeStage, setActiveStage] = useState(null);

  return (
    <div className="space-y-4">
      {framework.map((stage, index) => {
        const isActive = activeStage === index;
        
        return (
          <motion.div
            key={stage.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="overflow-hidden"
          >
            {/* Stage Header */}
            <button
              onClick={() => setActiveStage(isActive ? null : index)}
              className={`w-full text-left transition-all duration-300 ${
                isActive
                  ? 'bg-white text-navy-dark'
                  : 'bg-navy-card hover:bg-navy-card-hover border border-navy-border'
              } rounded-sm`}
            >
              <div className="p-8 flex items-center justify-between">
                <div className="flex items-center gap-6 flex-1">
                  {/* Stage Number */}
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold transition-all duration-300 ${
                      isActive
                        ? 'bg-navy-dark text-white'
                        : 'bg-white bg-opacity-5 text-white'
                    }`}
                  >
                    {index + 1}
                  </div>

                  {/* Stage Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      <h3 className="text-2xl font-bold tracking-tight">
                        {stage.stage}
                      </h3>
                      <span
                        className={`text-sm uppercase tracking-wider font-semibold ${
                          isActive ? 'text-navy-dark opacity-70' : 'text-gray-500'
                        }`}
                      >
                        {stage.title}
                      </span>
                    </div>
                    <p
                      className={`text-lg ${
                        isActive ? 'text-navy-dark opacity-80' : 'text-gray-400'
                      }`}
                    >
                      {stage.description}
                    </p>
                  </div>
                </div>

                {/* Expand Icon */}
                <motion.div
                  animate={{ rotate: isActive ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="ml-4"
                >
                  <ChevronDown className="w-6 h-6" />
                </motion.div>
              </div>
            </button>

            {/* Expanded Content */}
            <AnimatePresence>
              {isActive && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="bg-white bg-opacity-5 backdrop-blur-sm border-x border-b border-navy-border rounded-b-sm">
                    <div className="p-8 space-y-6">
                      {/* Details */}
                      <div>
                        <h4 className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-3">
                          Approach
                        </h4>
                        <p className="text-lg text-gray-300 leading-relaxed">
                          {stage.details}
                        </p>
                      </div>

                      {/* Outcomes */}
                      <div>
                        <h4 className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-4">
                          Key Outcomes
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {stage.outcomes.map((outcome, idx) => (
                            <div
                              key={idx}
                              className="flex items-start gap-3 bg-white bg-opacity-5 p-4 rounded-sm"
                            >
                              <CheckCircle2
                                className="w-5 h-5 text-white flex-shrink-0 mt-0.5"
                                strokeWidth={2}
                              />
                              <span className="text-sm text-gray-300 leading-relaxed">
                                {outcome}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

export default ExpandableFramework;
